import { useMemo, useState, type ReactNode } from 'react'
import {
  AlertTriangle, Ban, CircleAlert, Crown, Gift, HeartPulse,
  Mail, Phone, Star, Tag as TagIcon,
} from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { nights, todayISO } from '../lib/dates'
import {
  birthdayInfo, gapsFor, guestLabel, guestStats, vipStatus, type GuestProfile,
} from '../lib/guests'
import { Button, Card, Field, Input, Select, Sheet, cx } from '../ui/kit'
import GuestPrivacyPanel from './GuestPrivacyPanel'
import type { MarketingProfile } from '../lib/marketing'
import GuestStatsGrid, { birthdayWhen } from './GuestStats'
import GuestMarketingPanel from './GuestMarketingPanel'
import {
  DOC_TYPES, FLOORS, GAP_TEXT, HEALTH_BASIS, SALUTATIONS, SMOKING, SOURCES, policyOf,
} from './guestUi'

/**
 * Die Kartei eines Gastes — das ZWEITE Tempo.
 *
 * Im Buchungsblatt gibt es eine Schnellerfassung: Name tippen, „neu", weiter. Das ist
 * für den Moment richtig, in dem jemand am Telefon ein Zimmer will, und es erzeugt
 * einen halbfertigen Datensatz. Das ist der NORMALFALL, nicht die Ausnahme — jede
 * Portalbuchung liefert dasselbe. Dieses Blatt darf solche Datensätze deshalb nicht
 * wie Fehler behandeln; es zeigt stattdessen, was fehlt und was das konkret kostet
 * („ohne E-Mail keine Bestätigung möglich" statt eines stillen „unvollständig").
 *
 * Was hier NICHT eingetippt wird: Aufenthalte, Nächte, Umsatz, erster und letzter
 * Besuch. Diese Zahlen werden aus Buchungen, Rechnungen und Zahlungen gerechnet —
 * siehe `src/lib/guests.ts`. Gespeicherte Summen laufen auseinander, sobald jemand
 * eine Rechnung storniert.
 *
 * Texte inline, wie in `IcalCard` und `ComplianceCards`.
 */

export default function GuestSheet({
  guest, open, onClose,
}: { guest: GuestProfile | null; open: boolean; onClose: () => void }) {
  const { lang, formatDate, formatMoney } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const [draft, setDraft] = useState<GuestProfile | null>(null)
  const [saving, setSaving] = useState(false)

  // Der Entwurf wird beim ersten Rendern eines Gastes gesetzt und danach lokal
  // bearbeitet. Ohne diesen Vergleich würde jede Neuberechnung des Stores die
  // Eingabe des Vermieters überschreiben, während er noch tippt.
  const g = draft?.id === guest?.id ? draft : guest
  const patch = (p: Partial<GuestProfile>) => { if (g) setDraft({ ...g, ...p }) }

  const stats = useMemo(() => {
    if (!g) return null
    return guestStats({
      guestId: g.id,
      bookings: store.db.bookings,
      invoices: store.db.invoices,
      payments: store.db.payments,
      today: todayISO(),
    })
  }, [g?.id, store.db.bookings, store.db.invoices, store.db.payments])

  if (!g || !stats) return null

  const birthday = birthdayInfo(g.birthdate, todayISO())
  // Aus 0047 und deshalb noch nicht in `GuestProfile` — lokal gelesen, wie üblich.
  const marketingObjectedAt = (g as MarketingProfile).marketing_unsubscribed_at ?? null
  const policy = policyOf(store.db.tenant)
  const vip = vipStatus(stats, policy, g.vip_override)
  const gaps = gapsFor(g)
  const anonymised = Boolean(g.anonymized_at)

  const bookings = store.db.bookings
    .filter((b) => b.guest_id === g.id && b.kind !== 'block')
    .sort((a, b) => b.arrival.localeCompare(a.arrival))

  /**
   * Eine Gesundheitsangabe ohne Rechtsgrundlage lehnt schon die Datenbank ab
   * (CHECK `guests_health_needs_basis`). Das Speichern hier zu sperren ist kein
   * zweiter Riegel, sondern die verständliche Fassung desselben: eine rohe
   * Postgres-Fehlermeldung im Fehlerstreifen erklärt niemandem, was zu tun ist.
   */
  const healthIncomplete =
    (Boolean(g.allergies?.trim()) || Boolean(g.dietary?.trim())) && !g.health_basis

  const save = async () => {
    setSaving(true)
    await store.saveGuest(g)
    setSaving(false)
    onClose()
  }

  const tags = g.tags ?? []

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          {vip.vip && <Crown size={16} className="text-warn shrink-0" />}
          <span className="truncate">{guestLabel(g, de ? 'Ohne Namen' : 'No name')}</span>
        </span>
      }
      footer={
        <>
          <Button className="flex-1" variant="ghost" onClick={onClose}>
            {de ? 'Schließen' : 'Close'}
          </Button>
          <Button
            className="flex-1" variant="primary"
            onClick={() => void save()}
            disabled={saving || anonymised || healthIncomplete}
            title={healthIncomplete
              ? (de ? 'Bitte die Grundlage für die Gesundheitsangabe wählen.'
                : 'Please choose the basis for the health information.')
              : undefined}
          >
            {de ? 'Speichern' : 'Save'}
          </Button>
        </>
      }
    >
      {/* ── Kennzahlen: gerechnet, nie eingetippt ─────────────────────────── */}
      <GuestStatsGrid
        stats={stats}
        de={de}
        formatMoney={formatMoney}
        formatDate={formatDate}
        unitName={(id) => store.unitById(id)?.name ?? ''}
        birthday={birthday}
      />

      {/* ── Was fehlt, nach Nutzen sortiert ───────────────────────────────── */}
      {gaps.length > 0 && (
        <Card className="p-3 space-y-1.5">
          <div className="text-xs font-medium text-muted flex items-center gap-1.5">
            <CircleAlert size={13} />
            {de ? 'Noch nicht erfasst' : 'Not recorded yet'}
          </div>
          <ul className="space-y-1 text-sm">
            {gaps.map((gap) => (
              <li key={gap.code} className={cx(
                'flex gap-2',
                gap.weight === 'blocking' ? 'text-warn' : 'text-muted',
              )}>
                {gap.weight === 'blocking'
                  ? <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                  : <span className="w-[15px] shrink-0" />}
                <span>{de ? GAP_TEXT[gap.code].de : GAP_TEXT[gap.code].en}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {anonymised && (
        <p className="text-sm rounded-xl bg-surface-2 p-3">
          {de
            ? 'Dieser Datensatz ist anonymisiert und wird nicht mehr bearbeitet.'
            : 'This record has been anonymised and is no longer editable.'}
        </p>
      )}

      <fieldset disabled={anonymised} className="space-y-4 disabled:opacity-60">
        {/* ── Stammdaten ─────────────────────────────────────────────────── */}
        <Section title={de ? 'Stammdaten' : 'Master data'}>
          <div className="grid grid-cols-2 gap-3">
            <Field label={de ? 'Anrede' : 'Salutation'}>
              <Select value={g.salutation ?? ''}
                onChange={(e) => patch({ salutation: (e.target.value || null) as never })}>
                {SALUTATIONS.map((s) => (
                  <option key={s.value} value={s.value}>{de ? s.de : s.en}</option>
                ))}
              </Select>
            </Field>
            <Field label={de ? 'Titel' : 'Title'} hint={de ? 'Dr., Mag., …' : 'Dr., Prof., …'}>
              <Input value={g.title ?? ''} onChange={(e) => patch({ title: e.target.value })} />
            </Field>
            <Field label={de ? 'Vorname' : 'First name'}>
              <Input value={g.first_name ?? ''} onChange={(e) => patch({ first_name: e.target.value })} />
            </Field>
            <Field label={de ? 'Nachname' : 'Last name'}>
              <Input value={g.last_name ?? ''} onChange={(e) => patch({ last_name: e.target.value })} />
            </Field>
          </div>
          <Field label="E-Mail">
            <Input type="email" inputMode="email" value={g.email ?? ''}
              onChange={(e) => patch({ email: e.target.value })} />
          </Field>
          <Field label={de ? 'Telefon' : 'Phone'}>
            <Input type="tel" inputMode="tel" value={g.phone ?? ''}
              onChange={(e) => patch({ phone: e.target.value })} />
          </Field>
          <Field label={de ? 'Anschrift' : 'Address'}>
            <Input value={g.address ?? ''} onChange={(e) => patch({ address: e.target.value })} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label={de ? 'PLZ' : 'Postcode'}>
              <Input value={g.postcode ?? ''} onChange={(e) => patch({ postcode: e.target.value })} />
            </Field>
            <Field label={de ? 'Ort' : 'City'} className="col-span-2">
              <Input value={g.city ?? ''} onChange={(e) => patch({ city: e.target.value })} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label={de ? 'Land' : 'Country'} hint="AT, DE, …">
              <Input value={g.country ?? ''} maxLength={2}
                onChange={(e) => patch({ country: e.target.value.toUpperCase() })} />
            </Field>
            <Field
              label={de ? 'Staatsbürgerschaft' : 'Nationality'}
              hint={de ? 'für den Meldeschein' : 'for the guest register'}
            >
              <Input value={g.nationality ?? ''} maxLength={2}
                onChange={(e) => patch({ nationality: e.target.value.toUpperCase() })} />
            </Field>
            <Field label={de ? 'Sprache' : 'Language'}>
              <Select value={g.language ?? 'de'}
                onChange={(e) => patch({ language: e.target.value })}>
                <option value="de">Deutsch</option>
                <option value="en">English</option>
              </Select>
            </Field>
          </div>
          {/*
            Der Hinweis steht AM FELD und nicht nur oben in der Nebenzeile: wer das
            Geburtsdatum gerade eintippt, soll sofort sehen, ob er sich vertippt hat.
            „142 Jahre" fällt hier auf, in einer Kennzahlenzeile nicht.
          */}
          <Field
            label={de ? 'Geburtsdatum' : 'Date of birth'}
            hint={birthday
              ? (de
                ? `${birthday.age} Jahre — ${birthdayWhen(birthday, true)}, wird dann ${birthday.turning}.`
                : `${birthday.age} years — ${birthdayWhen(birthday, false)}, turning ${birthday.turning}.`)
              : undefined}
          >
            <Input type="date" value={g.birthdate ?? ''}
              onChange={(e) => patch({ birthdate: e.target.value || null })} />
          </Field>
        </Section>

        {/* ── Firma ──────────────────────────────────────────────────────── */}
        <Section
          title={de ? 'Geschäftsreise' : 'Business travel'}
          hint={de
            ? 'Nur ausfüllen, wenn die Rechnung an eine Firma geht.'
            : 'Only needed when the invoice goes to a company.'}
        >
          <Field label={de ? 'Firma' : 'Company'}>
            <Input value={g.company ?? ''} onChange={(e) => patch({ company: e.target.value })} />
          </Field>
          <Field label={de ? 'UID-Nummer' : 'VAT ID'}>
            <Input value={g.vat_id ?? ''} onChange={(e) => patch({ vat_id: e.target.value })} />
          </Field>
        </Section>

        {/* ── Ausweis ────────────────────────────────────────────────────── */}
        <Section
          title={de ? 'Ausweis' : 'Identity document'}
          hint={de
            ? 'Kommt beim Online-Check-in ohnehin herein; hier steht er für den nächsten Aufenthalt bereit.'
            : 'Collected at online check-in anyway; kept here for the next stay.'}
        >
          <div className="grid grid-cols-2 gap-3">
            <Field label={de ? 'Art' : 'Type'}>
              <Select value={g.id_document_type ?? ''}
                onChange={(e) => patch({ id_document_type: (e.target.value || null) as never })}>
                {DOC_TYPES.map((d) => (
                  <option key={d.value} value={d.value}>{de ? d.de : d.en}</option>
                ))}
              </Select>
            </Field>
            <Field label={de ? 'Nummer' : 'Number'}>
              <Input value={g.id_document ?? ''}
                onChange={(e) => patch({ id_document: e.target.value })} />
            </Field>
          </div>
        </Section>

        {/* ── Vorlieben ──────────────────────────────────────────────────── */}
        <Section
          title={de ? 'Vorlieben' : 'Preferences'}
          icon={<Star size={14} />}
          hint={de
            ? 'Das, was einen Stammgast merken lässt, dass man ihn kennt.'
            : 'The things that tell a returning guest you remember them.'}
        >
          <div className="grid grid-cols-2 gap-3">
            <Field label={de ? 'Etage' : 'Floor'}>
              <Select value={g.pref_floor ?? ''}
                onChange={(e) => patch({ pref_floor: (e.target.value || null) as never })}>
                {FLOORS.map((f) => (
                  <option key={f.value} value={f.value}>{de ? f.de : f.en}</option>
                ))}
              </Select>
            </Field>
            <Field label={de ? 'Rauchen' : 'Smoking'}>
              <Select value={g.pref_smoking ?? ''}
                onChange={(e) => patch({ pref_smoking: (e.target.value || null) as never })}>
                {SMOKING.map((s) => (
                  <option key={s.value} value={s.value}>{de ? s.de : s.en}</option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label={de ? 'Zimmerlage' : 'Room location'}
            hint={de ? 'z. B. „ruhig, hofseitig"' : 'e.g. “quiet, courtyard side”'}>
            <Input value={g.pref_location ?? ''}
              onChange={(e) => patch({ pref_location: e.target.value })} />
          </Field>
          <Field label={de ? 'Bett und Kissen' : 'Bed and pillows'}>
            <Input value={g.pref_bedding ?? ''}
              onChange={(e) => patch({ pref_bedding: e.target.value })} />
          </Field>
          <Field label={de ? 'Übliche Ankunftszeit' : 'Usual arrival time'}
            hint={de ? 'z. B. „meist spät, gegen 20 Uhr"' : 'e.g. “usually late, around 8 pm”'}>
            <Input value={g.pref_arrival_time ?? ''}
              onChange={(e) => patch({ pref_arrival_time: e.target.value })} />
          </Field>
          <Field label={de ? 'Sonstiges' : 'Anything else'}>
            <textarea
              className="w-full bg-surface-2 border border-border rounded-xl px-3 py-2.5 min-h-20"
              value={g.preferences_note ?? ''}
              onChange={(e) => patch({ preferences_note: e.target.value })}
            />
          </Field>
        </Section>

        {/* ── Art. 9 DSGVO ───────────────────────────────────────────────── */}
        <HealthSection guest={g} patch={patch} de={de} />

        {/* ── Anlass, Fahrzeug, Haustier ─────────────────────────────────── */}
        <Section title={de ? 'Zum Aufenthalt' : 'About the stay'} icon={<Gift size={14} />}>
          <div className="grid grid-cols-2 gap-3">
            <Field label={de ? 'Anlass' : 'Occasion'}
              hint={de ? 'Hochzeitstag, Jubiläum …' : 'Anniversary, celebration …'}>
              <Input value={g.occasion ?? ''} onChange={(e) => patch({ occasion: e.target.value })} />
            </Field>
            <Field label={de ? 'Datum dazu' : 'Its date'}>
              <Input type="date" value={g.occasion_date ?? ''}
                onChange={(e) => patch({ occasion_date: e.target.value || null })} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={de ? 'Kennzeichen' : 'Number plate'}
              hint={de ? 'für den Stellplatz' : 'for the parking space'}>
              <Input value={g.vehicle_plate ?? ''}
                onChange={(e) => patch({ vehicle_plate: e.target.value.toUpperCase() })} />
            </Field>
            <Field label={de ? 'Fahrzeug' : 'Vehicle'}>
              <Input value={g.vehicle_note ?? ''}
                onChange={(e) => patch({ vehicle_note: e.target.value })} />
            </Field>
          </div>
          <Field label={de ? 'Haustier' : 'Pet'}>
            <Input value={g.pets ?? ''} onChange={(e) => patch({ pets: e.target.value })} />
          </Field>
          <Field label={de ? 'Wie er zu uns kam' : 'How they found us'}>
            <Select value={SOURCES.some((s) => s.value === (g.source ?? '')) ? (g.source ?? '') : ''}
              onChange={(e) => patch({ source: e.target.value || null })}>
              {SOURCES.map((s) => (
                <option key={s.value} value={s.value}>{de ? s.de : s.en}</option>
              ))}
            </Select>
          </Field>
        </Section>

        {/* ── Schlagworte ────────────────────────────────────────────────── */}
        <Section
          title={de ? 'Schlagworte' : 'Tags'}
          icon={<TagIcon size={14} />}
          hint={de
            ? 'Für alles, wofür es kein Feld gibt: „Skikurs", „zahlt bar", „Firmenkunde Meier".'
            : 'For everything without a field of its own.'}
        >
          <TagEditor tags={tags} onChange={(next) => patch({ tags: next })} de={de} />
        </Section>

        {/* ── Einwilligung und Sperrvermerk ──────────────────────────────── */}
        <Section title={de ? 'Einwilligung und Vermerke' : 'Consent and flags'}>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox" className="mt-1"
              checked={Boolean(g.marketing_consent)}
              onChange={(e) => patch({
                marketing_consent: e.target.checked,
                // Art. 7 Abs. 1 DSGVO verlangt, dass sich die Einwilligung NACHWEISEN
                // lässt. Ein Häkchen ohne Datum ist kein Nachweis — also wird es beim
                // Setzen mitgeschrieben und beim Widerruf wieder entfernt.
                marketing_consent_at: e.target.checked ? new Date().toISOString() : null,
                marketing_consent_source: e.target.checked
                  ? (g.marketing_consent_source ?? (de ? 'Von Hand erfasst' : 'Entered manually'))
                  : null,
              })}
            />
            <span>
              {de ? 'Darf Werbung erhalten' : 'May receive marketing'}
              {g.marketing_consent && g.marketing_consent_at && (
                <span className="block text-xs text-muted">
                  {de ? 'erteilt am ' : 'given on '}
                  {formatDate(g.marketing_consent_at.slice(0, 10))}
                  {g.marketing_consent_source ? ` · ${g.marketing_consent_source}` : ''}
                </span>
              )}
            </span>
          </label>
          {g.marketing_consent && (
            <Field label={de ? 'Woher stammt die Einwilligung?' : 'Where did the consent come from?'}
              hint={de
                ? 'Der Nachweis muss den Weg UND den Wortlaut nennen — „Anfrageformular 03/2026, Feld „Ich möchte Neuigkeiten erhalten"" ist ein Nachweis, „Website" ist keiner.'
                : 'The proof must name the channel AND the wording it was given under.'}>
              <Input value={g.marketing_consent_source ?? ''}
                onChange={(e) => patch({ marketing_consent_source: e.target.value })} />
            </Field>
          )}

          {/*
            Der Widerspruch steht ÜBER dem Häkchen und lässt sich nicht durch
            Anhaken wegwischen. Wer ihm einmal widersprochen hat, ist kein Gast, den
            man versehentlich wieder in den Verteiler nimmt — das Häkchen allein
            reicht deshalb nicht, es braucht die ausdrückliche Aufhebung mit dem
            Weg, auf dem die neue Einwilligung erteilt wurde.
          */}
          {marketingObjectedAt && (
            <div className="rounded-xl border border-warn/40 bg-warn-soft/25 p-3 space-y-2">
              <p className="text-sm">
                {de
                  ? `Dieser Gast hat am ${formatDate(marketingObjectedAt.slice(0, 10))} der Werbung widersprochen.`
                  : `This guest objected to marketing on ${formatDate(marketingObjectedAt.slice(0, 10))}.`}
              </p>
              <p className="text-xs text-muted">
                {de
                  ? 'Solange das hier steht, geht keine Werbung hinaus — auch mit gesetztem Häkchen nicht. Aufheben lässt es sich nur, wenn der Gast SELBST neu eingewilligt hat; tragen Sie dann ein, wie und wann.'
                  : 'While this stands, no marketing goes out — even with the box ticked. It can only be lifted if the guest has consented again.'}
              </p>
              <Button
                size="sm" type="button"
                onClick={() => patch({
                  marketing_unsubscribed_at: null,
                  marketing_unsubscribed_source: null,
                  marketing_consent: true,
                  marketing_consent_at: new Date().toISOString(),
                  marketing_consent_source: de
                    ? 'Neu erteilt nach Widerspruch — Weg bitte eintragen'
                    : 'Renewed after objection — please state the channel',
                } as Partial<GuestProfile>)}
              >
                {de ? 'Der Gast hat neu eingewilligt' : 'The guest consented again'}
              </Button>
            </div>
          )}

          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" checked={Boolean(g.blacklisted)}
              onChange={(e) => patch({ blacklisted: e.target.checked })} />
            <span className="inline-flex items-center gap-1.5">
              <Ban size={14} className={g.blacklisted ? 'text-danger' : 'text-muted'} />
              {de ? 'Sperrvermerk' : 'Blocked'}
            </span>
          </label>

          <Field label={de ? 'Interne Notiz' : 'Internal note'}
            hint={de
              ? 'Sieht nur der Betrieb. Gesundheitliches gehört trotzdem in das Feld oben, nicht hierher.'
              : 'Internal only. Health information still belongs in the field above, not here.'}>
            <textarea
              className="w-full bg-surface-2 border border-border rounded-xl px-3 py-2.5 min-h-24"
              value={g.notes ?? ''}
              onChange={(e) => patch({ notes: e.target.value })}
            />
          </Field>
        </Section>

        {/* ── VIP ────────────────────────────────────────────────────────── */}
        <Section
          title={de ? 'Stammgast / VIP' : 'VIP'}
          icon={<Crown size={14} />}
          hint={vipDescription(policy, de)}
        >
          <Select
            value={g.vip_override === true ? 'on' : g.vip_override === false ? 'off' : 'auto'}
            onChange={(e) => patch({
              vip_override: e.target.value === 'on' ? true
                : e.target.value === 'off' ? false : null,
            })}
          >
            <option value="auto">
              {de
                ? `Automatisch — derzeit ${vip.automatic ? 'ja' : 'nein'}`
                : `Automatic — currently ${vip.automatic ? 'yes' : 'no'}`}
            </option>
            <option value="on">{de ? 'Immer VIP' : 'Always VIP'}</option>
            <option value="off">{de ? 'Nie VIP' : 'Never VIP'}</option>
          </Select>
          {vip.source === 'manual_on' && !vip.automatic && (
            <p className="text-xs text-muted">
              {de
                ? 'Von Hand gesetzt: dieser Gast erreicht die Schwelle nicht, gilt aber trotzdem als VIP.'
                : 'Set by hand: this guest is below the threshold but counts as a VIP anyway.'}
            </p>
          )}
          {vip.source === 'manual_off' && vip.automatic && (
            <p className="text-xs text-muted">
              {de
                ? 'Von Hand abgeschaltet: dieser Gast läge über der Schwelle.'
                : 'Switched off by hand: this guest would be above the threshold.'}
            </p>
          )}
        </Section>
      </fieldset>

      {/* ── Aufenthalte ──────────────────────────────────────────────────── */}
      {bookings.length > 0 && (
        <Section title={de ? 'Aufenthalte' : 'Stays'}>
          <ul className="divide-y divide-border rounded-xl border border-border overflow-hidden">
            {bookings.slice(0, 12).map((b) => (
              <li key={b.id} className="p-3 flex items-center gap-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="truncate">{store.unitById(b.unit_id)?.name ?? '—'}</div>
                  <div className="text-xs text-muted tabular-nums">
                    {formatDate(b.arrival)} – {formatDate(b.departure)}
                    {' · '}{nights(b.arrival, b.departure)} {de ? 'N.' : 'n.'}
                    {' · '}{b.reference}
                  </div>
                </div>
                <span className="tabular-nums shrink-0">{formatMoney(b.total_gross, b.currency)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/*
        Werbung steht NACH den Aufenthalten und VOR dem Datenschutzkasten: sie ist
        eine Handlung, die man an diesem Gast vornimmt, und die will man erst
        erwägen, wenn man weiß, wer er ist. Nicht ganz unten, weil sie sonst nur der
        findet, der ohnehin scrollt.
      */}
      <GuestMarketingPanel guest={g} onChanged={onClose} />

      <GuestPrivacyPanel guest={g} onChanged={onClose} />
    </Sheet>
  )
}

/* ─────────────────────────────────────────────────────── Gesundheitsdaten */

/**
 * Allergien und Ernährung — und der Grund, warum sie einen eigenen Kasten haben.
 *
 * Das sind besondere Kategorien personenbezogener Daten nach Art. 9 Abs. 1 DSGVO.
 * Sie zwischen Kissenwunsch und Kennzeichen zu stellen wäre bequem und falsch: der
 * Vermieter soll SEHEN, dass er hier etwas erhebt, das anderen Regeln folgt. Deshalb
 * der farbige Rahmen, deshalb die Pflichtangabe zur Rechtsgrundlage — ohne sie
 * lehnt schon die Datenbank den Wert ab (CHECK `guests_health_needs_basis`).
 *
 * Kein Rechtsrat, sondern die Sachlage: erhoben nur mit Einwilligung des Gastes oder
 * soweit für die gebuchte Leistung erforderlich, und nicht länger als nötig.
 */
function HealthSection({
  guest, patch, de,
}: {
  guest: GuestProfile
  patch: (p: Partial<GuestProfile>) => void
  de: boolean
}) {
  const filled = Boolean(guest.allergies?.trim()) || Boolean(guest.dietary?.trim())

  // Sobald etwas eingetragen wird, muss die Grundlage mit. Das Datum der Erhebung
  // schreibt die Maske selbst — es ist Teil des Nachweises und nicht Tipparbeit.
  const touch = (p: Partial<GuestProfile>) => {
    const next = { ...guest, ...p }
    const nowFilled = Boolean(next.allergies?.trim()) || Boolean(next.dietary?.trim())
    patch({
      ...p,
      ...(nowFilled && !next.health_recorded_at
        ? { health_recorded_at: new Date().toISOString() }
        : {}),
      ...(nowFilled ? {} : { health_basis: null, health_recorded_at: null }),
    })
  }

  return (
    <div className="rounded-2xl border-2 border-danger/30 bg-danger-soft/25 p-3 space-y-3">
      <h3 className="font-medium flex items-center gap-2">
        <HeartPulse size={15} className="text-danger" />
        {de ? 'Allergien und Ernährung' : 'Allergies and diet'}
      </h3>
      <p className="text-xs text-muted">
        {de
          ? 'Besondere Kategorie personenbezogener Daten nach Art. 9 DSGVO. Diese Angaben dürfen nur erhoben werden, wenn der Gast ausdrücklich eingewilligt hat oder sie für die gebuchte Leistung erforderlich sind — und nur so lange, wie sie gebraucht werden. Auch eine Ernährungsangabe kann darunterfallen, weil sie auf Gesundheit oder Religion schließen lässt.'
          : 'Special category of personal data under Art. 9 GDPR. Only record this with the guest’s explicit consent or where it is necessary for the booked service — and only for as long as it is needed. Dietary notes count too: they can reveal health or religion.'}
      </p>

      <Field label={de ? 'Allergien' : 'Allergies'}>
        <Input value={guest.allergies ?? ''}
          onChange={(e) => touch({ allergies: e.target.value })} />
      </Field>
      <Field label={de ? 'Ernährung' : 'Diet'}>
        <Input value={guest.dietary ?? ''}
          onChange={(e) => touch({ dietary: e.target.value })} />
      </Field>

      {filled && (
        <Field
          label={de ? 'Auf welcher Grundlage?' : 'On what basis?'}
          hint={de
            ? 'Pflichtangabe. Ohne sie speichert die Datenbank die Angabe nicht.'
            : 'Required. Without it the database refuses the entry.'}
        >
          <Select value={guest.health_basis ?? ''}
            onChange={(e) => patch({ health_basis: (e.target.value || null) as never })}>
            {HEALTH_BASIS.map((h) => (
              <option key={h.value} value={h.value}>{de ? h.de : h.en}</option>
            ))}
          </Select>
        </Field>
      )}
      {filled && !guest.health_basis && (
        <p className="text-xs text-danger">
          {de
            ? 'Bitte die Grundlage angeben — sonst lässt sich der Eintrag nicht speichern.'
            : 'Please state the basis — otherwise this cannot be saved.'}
        </p>
      )}
    </div>
  )
}

/* ─────────────────────────────────────────────────────── Kleinteile */

function Section({
  title, hint, icon, children,
}: {
  title: string
  hint?: string
  icon?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="space-y-3">
      <div>
        <h3 className="font-medium flex items-center gap-2">{icon}{title}</h3>
        {hint && <p className="text-xs text-muted mt-0.5">{hint}</p>}
      </div>
      {children}
    </section>
  )
}

function TagEditor({
  tags, onChange, de,
}: { tags: string[]; onChange: (next: string[]) => void; de: boolean }) {
  const [value, setValue] = useState('')
  const add = () => {
    const next = value.trim()
    if (!next || tags.includes(next)) { setValue(''); return }
    onChange([...tags, next])
    setValue('')
  }
  return (
    <div className="space-y-2">
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map((t) => (
            <button
              key={t} type="button"
              onClick={() => onChange(tags.filter((x) => x !== t))}
              className="px-2 py-0.5 rounded-full text-xs bg-accent-soft text-accent"
            >
              {t} ×
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          value={value}
          placeholder={de ? 'Schlagwort hinzufügen …' : 'Add a tag …'}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
        />
        <Button size="sm" type="button" onClick={add}>{de ? 'Hinzu' : 'Add'}</Button>
      </div>
    </div>
  )
}

function vipDescription(
  policy: ReturnType<typeof policyOf>, de: boolean,
): string {
  const parts: string[] = []
  if (policy.vipRevenueThreshold) {
    parts.push(de
      ? `ab ${policy.vipRevenueThreshold} € Gesamtumsatz`
      : `from ${policy.vipRevenueThreshold} in total revenue`)
  }
  if (policy.vipStayThreshold) {
    parts.push(de
      ? `ab ${policy.vipStayThreshold} Aufenthalten`
      : `from ${policy.vipStayThreshold} stays`)
  }
  if (parts.length === 0) {
    return de
      ? 'Es ist keine Schwelle festgelegt — VIP gilt nur, wenn er hier von Hand gesetzt wird.'
      : 'No threshold configured — VIP only applies when set by hand here.'
  }
  return de
    ? `Automatisch ${parts.join(' oder ')}. Von Hand geht immer.`
    : `Automatic ${parts.join(' or ')}. Manual always wins.`
}

/** Für die Liste: die Kontaktzeile, die auf dem Handy noch lesbar ist. */
export function GuestContactLine({ guest }: { guest: GuestProfile }) {
  return (
    <span className="flex items-center gap-3 min-w-0">
      {guest.email && (
        <span className="inline-flex items-center gap-1 min-w-0">
          <Mail size={11} className="shrink-0" />
          <span className="truncate">{guest.email}</span>
        </span>
      )}
      {guest.phone && (
        <span className="inline-flex items-center gap-1 shrink-0">
          <Phone size={11} />{guest.phone}
        </span>
      )}
    </span>
  )
}
